import React, {useContext, useEffect, useRef, useState} from 'react';
import {FaExternalLinkAlt, FaQuestionCircle} from "react-icons/fa";
import {useQuery} from "@tanstack/react-query";
import moment from "moment";
import {toast} from "react-toastify";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {useNavigate, useParams} from "react-router-dom";
import projectService from "../../../services/project-service";
import {AlertMessage} from "../../../utils/alert-message";
import TemplateDefault from "../../../components/template-default";
import Card from "../../../components/card";
import InputForm from "../../../components/input-form";
import SelectComponent from "../../../components/select-component";
import LicenceTable from "../components/licence-table";
import ContactsTable from "../components/contacts-table";
import AttachesTable from "../../../components/attaches-table";
import InputText from "../../../components/input-text";
import {MaskDefault} from "../../../utils/mask";
import Button from "../../../components/button";
import SshTable from "../components/ssh-table";
import {ProjectContext} from '../../../context/project-context';
import SshService from "../../../services/ssh-service";
import SshModal from "../../../components/ssh-modal";
import EquipmentTable from "../components/equipment-table";
import ResumeCard from "../components/resume-card";
import CloneComponent from "../components/clone-component";
import userService from "../../../services/user-service";
import InputCheckbox from "../../../components/input-checkbox";
import InputDateTime from "../../../components/input-datetime";

const MySwal = withReactContent(Swal);

const isExpirationDateAllowed = (date) => {
    const isBlockedWeekday = [0, 1, 5, 6].includes(date.getDay());
    const isSeptemberEighth = date.getDate() === 8 && date.getMonth() === 8;

    return !isBlockedWeekday && !isSeptemberEighth;
};
const CURRENT_YEAR = new Date().getFullYear();
const HOLIDAYS_YEARS = Array.from({length: 3}, (_, index) => CURRENT_YEAR + index);
const HOLIDAYS_API_URL = (year) => `https://brasilapi.com.br/api/feriados/v1/${year}`;

const initialData = {
    dead_file: false,
    name: "",
    observation: "",
    ssh: "",
    activation_date: "",
    expiration_date: "",
    answer: "",
    commercial_contact_id: "",
    phone: "",
    info: "",
    project_licensing: [],
    project_ssh: [],
    contacts: [],
    attachments: [],
    resales: [],
    type_license: undefined,
    modality: undefined,
    related_ibge: "",
    type_project: undefined,
    code: "",
    total_active_lane: "",
    install_observation: "",
    equipment: [],
    multiway_link: "",
    link_attachments: [],
    category: undefined,
    department: undefined,
    ssh_warning: "",
    resale_only: false
}

const ProjectForm = () => {
    const {id} = useParams();
    const {user} = useContext(ProjectContext);

    const navigate = useNavigate();

    const licencesRef = useRef();
    const contactsRef = useRef();
    const equipmentRef = useRef();
    const attachesRef = useRef();
    const linkAttachesRef = useRef();
    const sshRef = useRef();
    const multiwayLinkRef = useRef(null);
    const installObservationRef = useRef(null);
    const resumeLinkRef = useRef(null);
    const resumeObservationRef = useRef(null);
    const pendingExpirationDateRef = useRef(undefined);

    const [state, setState] = useState(initialData);
    const [isLoading, setIsLoading] = useState(false);
    const [sshModal, setSshModal] = useState(false);
    const [showCard, setShowCard] = useState("resume")
    const [multiwayLinkHeight, setMultiwayLinkHeight] = useState("");
    const [instObsHeight, setInstObsHeight] = useState("");
    const [excludedExpirationDates, setExcludedExpirationDates] = useState([]);

    const excludePermission = user?.permissions?.find((element) => element === "exclude_projects") || null;
    const hasCitiesPermission = user?.permissions?.includes("cities");
    const isReadOnlyProject = user?.permissions?.includes("licenses_history") && !hasCitiesPermission;
    const canChangeClone = user?.permissions?.includes("change_clone");

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        const logHolidayDates = async () => {
            try {
                const results = await Promise.allSettled(
                    HOLIDAYS_YEARS.map(async (year) => {
                        const response = await fetch(HOLIDAYS_API_URL(year), {signal: controller.signal});

                        return response.json();
                    })
                );

                if (controller.signal.aborted) return;

                results
                    .filter(({status}) => status === "rejected")
                    .forEach(({reason}) => console.error("Erro ao consultar feriados na BrasilAPI:", reason));

                const holidays = results.flatMap((result) =>
                    result.status === "fulfilled" && Array.isArray(result.value) ? result.value : []
                );
                const holidayDates = holidays.reduce((dates, {date}) => {
                    const parsedDate = moment(date, "YYYY-MM-DD", true);

                    if (parsedDate.isValid()) dates.push(parsedDate.toDate());

                    return dates;
                }, []);

                setExcludedExpirationDates(holidayDates);
            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error("Não foi possível consultar as datas de feriados na BrasilAPI:", error);
                }
            }
        };

        logHolidayDates();

        return () => controller.abort();
    }, []);

    const {data: options} = useQuery({
        queryFn: () => projectService.form_options(id ?? 0).then(({data}) => data),
        queryKey: ['options', {id}],
    });

    const {data: commercialOpt} = useQuery({
        queryFn: () => userService.getCommercialUsers().then(({data}) => data),
        initialData: [],
        queryKey: ['commercialOpt'],
    });

    const resales = options?.resales.filter(v => v.value !== 1);

    const {refetch, isError} = useQuery({
        queryFn: () => projectService.getById(id).then((data) => {
            const commercial_contact = commercialOpt.find(c => c.value === data.commercial_contact_id);
            data.commercial_contact_id = commercial_contact ?? "";
            data.project_licensing = data.project_licensing?.map(p => ({...p, date_start: moment(p.date_start).format("DD/MM/YYYY")}));
            data.contacts = JSON.parse(data.contacts);
            data.attachments = JSON.parse(data.attachments);
            data.equipment = JSON.parse(data.equipment);
            data.link_attachments = JSON.parse(data.link_attachments);
            data.resales = data.resales.filter(v => v.value !== 1);
            data.activation_date = data.activation_date ? moment(data.activation_date).format("YYYY-MM-DD") : "";
            data.expiration_date = data.expiration_date ? moment(data.expiration_date).format("YYYY-MM-DD") : "";
            data.answer = data.answer ? moment(data.answer).format("YYYY-MM-DD") : "";

            Object.keys(data).forEach(key => data[key] = data[key] ?? "");

            data.contacts.map(c => {
                c.weekly_report = c?.weekly_report ?? false
                return c
            })

            setState(data);
            return data;
        }),
        queryKey: ['projects', {id}, {commercialOpt}],
        enabled: !!id,
        retry: 0
    });

    if (isError) navigate("/404");

    const submit = () => {
        const formData = new FormData();
        console.log(state)

        Object.keys(state)
            .filter(key => !["resales", "project_licensing", "contacts", "attachments"].includes(key))
            .forEach(key => {
                let data = state[key];
                if (typeof data === 'object') data = data?.value ?? "";

                formData.append(key, data);
            });

        const licencesRow = [];
        let licencesEmptyField = false;
        licencesRef.current?.api.forEachNode((node) => {
            licencesEmptyField = Object.keys(node.data)
                .filter(key => ["cameras", "users", "ocr", "contactid", "mobile"].includes(key))
                .some(key => node.data[key] === null || node.data[key] === "" || node.data[key] === undefined);

            licencesRow.push(node.data);
        });

        if (licencesEmptyField) {
            toast.warning(AlertMessage.licencesEmptyField);
            return;
        }

        const contactsRow = [];
        contactsRef.current?.api.forEachNode((node) => contactsRow.push(node.data));

        const equipmentRow = [];
        equipmentRef.current?.api.forEachNode((node) => equipmentRow.push(node.data));

        const attachesRow = [];
        attachesRef.current?.api.forEachNode((node) => {
            attachesRow.push(node.data);

            if (node.data?.file) formData.append("attaches[]", node.data.file);
        });

        const linkAttachesRow = [];
        linkAttachesRef.current?.api.forEachNode((node) => {
            linkAttachesRow.push(node.data);

            if (node.data?.file) formData.append("link_attaches[]", node.data.file);
        });

        const sshRow = [];
        let sshEmptyField = false;
        sshRef.current?.api.forEachNode(({data}) => {
            if (!data.name || !data.ip || !data.type) sshEmptyField = true;

            const types = {
                "Direto": "DIRECT",
                "Via jump server": "ACROSS_JUMP_SERVER",
                "Jump server": "JUMP_SERVER"
            }

            const newData = {...data}
            newData.type = types[newData.type] ?? null;

            sshRow.push(newData);
        });

        if (sshEmptyField) {
            toast.warning(AlertMessage.sshEmptyField);
            return;
        }

        setIsLoading(true);

        formData.append("resales", JSON.stringify(state.resales.map(r => r.value)));
        formData.append("licences", JSON.stringify(licencesRow));
        formData.append("contacts", JSON.stringify(contactsRow));
        formData.append("attachments", JSON.stringify(attachesRow));
        formData.append("link_attachments", JSON.stringify(linkAttachesRow));
        formData.append("ssh", JSON.stringify(sshRow));
        formData.append("equipment", JSON.stringify(equipmentRow));

        if (!!id) formData.append('_method', 'PUT');

        const response = !!id ? projectService.edit(formData, id) : projectService.create(formData);

        response
            .then(({data}) => {
                toast.success("Salvo com sucesso.");
                id ? refetch() : navigate(`/projects/form/${data.id}`);
            })
            .catch((err) => {
                const {data: {message}} = err.response;
                if (message.includes("O número de telefone")) toast.error(message);
                else toast.error(AlertMessage[message] ?? "Não foi possível salvar");
            })
            .finally(() => setIsLoading(false));
    }

    const deleteProject = (e) => {
        e.preventDefault();

        MySwal.fire({
            icon: "warning",
            title: "Deseja realmente excluir o projeto?",
            showCancelButton: true,
            text: "Essa ação não poderá ser desfeita e todos os dados relacionados a ele serão apagados!",
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: '#e64942',
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                setIsLoading(true);

                projectService.delete(id)
                    .then(() => {
                        navigate(`/projects`);
                    })
                    .catch((err) => {
                        const {data: {message}} = err.response;
                        toast.error(AlertMessage[message] ?? "Não foi possível excluir");
                    })
                    .finally(() => setIsLoading(false));
            }
        });
    }

    const cancel = (e) => {
        e.preventDefault();
        navigate("/projects");
    }

    const handleInput = ({target: {id, value}}) => setState(prev => ({...prev, [id]: value}));
    const handleDateInput = (id, date) => setState(prev => ({
        ...prev,
        [id]: date ? moment(date).format("YYYY-MM-DD") : ""
    }));
    const hasHolidayInWeek = (date) => {
        const weekStart = moment(date).startOf("isoWeek");
        const weekEnd = moment(date).endOf("isoWeek");

        return excludedExpirationDates.some((holiday) =>
            isExpirationDateAllowed(holiday) &&
            moment(holiday).isBetween(weekStart, weekEnd, "day", "[]")
        );
    };
    const findNextExpirationDate = (date) => {
        const nextWeekStart = moment(date).add(1, "week").startOf("isoWeek");

        for (let weekOffset = 0; weekOffset < 52; weekOffset++) {
            const weekStart = moment(nextWeekStart).add(weekOffset, "weeks");

            if (hasHolidayInWeek(weekStart)) continue;

            for (const dayOffset of [1, 2, 3]) {
                const candidate = moment(weekStart).add(dayOffset, "days");
                const isExcludedDate = excludedExpirationDates.some((excludedDate) =>
                    candidate.isSame(excludedDate, "day")
                );

                if (isExpirationDateAllowed(candidate.toDate()) && !isExcludedDate) {
                    return candidate.toDate();
                }
            }
        }

        return null;
    };
    const validateExpirationDate = (date) => {
        if (!date) {
            handleDateInput("expiration_date", null);
            return;
        }

        if (hasHolidayInWeek(date)) {
            const nextExpirationDate = findNextExpirationDate(date);

            MySwal.fire({
                icon: "warning",
                html: `Não foi possivel selecionar esta data.<br>A nova "Data de expiração" foi postergada para a proxima semana.`
            });

            if (nextExpirationDate) {
                handleDateInput("expiration_date", nextExpirationDate);
            }

            return;
        }

        const isExcludedDate = excludedExpirationDates.some((excludedDate) =>
            moment(date).isSame(excludedDate, "day")
        );

        if (!isExpirationDateAllowed(date) || isExcludedDate) return;

        handleDateInput("expiration_date", date);
    };
    const handleExpirationDateChange = (date, event) => {
        const isTyping = event?.type === "change" ||
            (!event && document.activeElement?.id === "expiration_date");

        if (isTyping) {
            pendingExpirationDateRef.current = date;
            return;
        }

        pendingExpirationDateRef.current = undefined;
        validateExpirationDate(date);
    };
    const handleExpirationDateBlur = () => {
        setTimeout(() => {
            if (pendingExpirationDateRef.current === undefined) return;

            const pendingDate = pendingExpirationDateRef.current;
            pendingExpirationDateRef.current = undefined;
            validateExpirationDate(pendingDate);
        }, 0);
    };
    const handleSelect = (id, value) => setState(prev => ({...prev, [id]: value}));
    const handleInputMask = ({target: {value, id}}) => {
        const clearValue = value.replace(/[.,\s]/g, "");
        setState(prev => ({...prev, [id]: clearValue}));
    }
    const openSsh = () => {
        if (state.project_ssh.length === 0) {
            toast.error(AlertMessage.invalidSsh);
            return;
        }

        if (state.project_ssh.length > 1) {
            setSshModal(true);
            return;
        }

        const ssh = state.project_ssh;
        SshService.openSshConnection(id, ssh[0]?.id);
    }
    const className = "flex justify-left h-[35px] items-center focus:ring-2 focus:outline-none font-medium " +
        "rounded text-sm px-4 py-2 text-center disabled:opacity-60 cursor-pointer " +
        "hover:bg-gray-200 disabled:hover::bg-white"

    useEffect(() => {
        if (multiwayLinkRef.current && document.activeElement !== multiwayLinkRef.current && state.multiway_link !== "" && multiwayLinkRef.current.scrollHeight > 138) {
            multiwayLinkRef.current.style.height = 'auto';
            multiwayLinkRef.current.style.height = multiwayLinkHeight;
        }
    }, [multiwayLinkHeight, showCard, state.multiway_link]);

    useEffect(() => {
        if (installObservationRef.current && document.activeElement !== installObservationRef.current && state.install_observation !== "" && installObservationRef.current.scrollHeight > 138) {
            installObservationRef.current.style.height = 'auto';
            installObservationRef.current.style.height = instObsHeight;
        }
    }, [instObsHeight, showCard, state.install_observation]);

    const handleKeyDown = (e) => {
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();

            const { selectionStart, selectionEnd } = e.target;
            const value = e.target.value;

            if (e.target.id === "multiway_link"){
                const newValue = value.substring(0, selectionStart) + '    ' + value.substring(selectionEnd);

                setState(prev => ({...prev, multiway_link: newValue}))

                setTimeout(() => {
                    e.target.selectionStart = e.target.selectionEnd = selectionStart + 4;
                }, 0);
            }else if (e.target.id === "install_observation"){
                const newValue = value.substring(0, selectionStart) + '    ' + value.substring(selectionStart);

                setState(prev => ({...prev, install_observation: newValue}))

                setTimeout(() => {
                    e.target.selectionStart = e.target.selectionEnd = selectionStart + 4;
                }, 0);
            }
        }

        if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();

            const { selectionStart } = e.target;
            const start = selectionStart;
            const value = e.target.value;

            if (e.target.id === "multiway_link"){
                if (start >= 4) {
                    const prefix = value.substring(start - 4, start);

                    if (prefix === '    ') {
                        const newValue =
                            value.substring(0, start - 4) +
                            value.substring(start);

                        setState(prev => ({...prev, multiway_link: newValue}))

                        setTimeout(() => {
                            e.target.selectionStart = e.target.selectionEnd = selectionStart - 4;
                        }, 0);
                    }
                }
            }else if (e.target.id === "install_observation"){
                if (start >= 4) {
                    const prefix = value.substring(start - 4, start);

                    if (prefix === '    ') {
                        const newValue =
                            value.substring(0, start - 4) +
                            value.substring(start);

                        setState(prev => ({...prev, install_observation: newValue}))

                        setTimeout(() => {
                            e.target.selectionStart = e.target.selectionEnd = selectionStart - 4;
                        }, 0);
                    }
                }
            }
        }
    };

    const onClose = (setOpen, setNewRow, gridRef, editingRowIndex, modalData, needSubmit = false) => {
        setOpen(false)
        setNewRow(false)

        if (gridRef.current?.api) {
            const rowNode = gridRef.current.api.getRowNode(String(editingRowIndex));
            if (rowNode) {
                rowNode.setData(modalData);
            }
        }
        if (needSubmit) {
            submit()
        }
    }

    const onlyClose = (setOpen, setNewRow) => {
        setOpen(false)
        setNewRow(false)
    }

    return (
        <TemplateDefault title={(isReadOnlyProject ? "Projeto" : "Cadastro de projeto") + (id ? ` ${state.name}` : "")} isLoading={isLoading}>
            <div className="flex w-full overflow-hidden h-[calc(100vh-71px)]">
                <div className="shrink-0 h-full">
                    <Card wrapperClass="h-full flex flex-col" bodyClass="flex-1 overflow-y-auto">
                        <p onClick={() => setShowCard("resume")} className={`${className} ${showCard === "resume" ? "bg-gray-200" : ""}`}>
                            Resumo
                        </p>
                        {
                            !isReadOnlyProject &&
                            <>
                        <p onClick={() => setShowCard("data")} className={`${className} ${showCard === "data" ? "bg-gray-200" : ""}`}>
                            Dados
                        </p>
                        <p onClick={() => setShowCard("license")} className={`${className} ${showCard === "license" ? "bg-gray-200" : ""}`}>
                            Licenças
                        </p>
                        <p onClick={() => setShowCard("contacts")} className={`${className} ${showCard === "contacts" ? "bg-gray-200" : ""}`}>
                            Contatos
                        </p>
                        <p onClick={() => setShowCard("ssh")} className={`${className} ${showCard === "ssh" ? "bg-gray-200" : ""}`}>
                            SSH
                        </p>
                        <p onClick={() => setShowCard("docs")} className={`${className} ${showCard === "docs" ? "bg-gray-200" : ""}`}>
                            Documentos
                        </p>
                        <p onClick={() => setShowCard("inventory")} className={`${className} ${showCard === "inventory" ? "bg-gray-200" : ""}`}>
                            Inventário
                        </p>
                        {
                            canChangeClone &&
                            <p onClick={() => setShowCard("clone")} className={`${className} ${showCard === "clone" ? "bg-gray-200" : ""}`}>
                                Clone
                            </p>
                        }
                            </>
                        }
                    </Card>
                </div>
                <div className="flex-1 h-full overflow-auto pl-4">
                    <div className="w-full h-full overflow-y-auto pr-2">
                        {
                            showCard === "resume" &&
                            <ResumeCard
                                state={state}
                                resales={resales}
                                resumeObservationRef={resumeObservationRef}
                                resumeLinkRef={resumeLinkRef}
                                setMultiwayLinkHeight={setMultiwayLinkHeight}
                                setInstObsHeight={setInstObsHeight}
                                options={options}
                                commercialOpt={commercialOpt}
                            />
                        }

                        {
                            showCard === "data" &&
                            <>
                                <Card title="Dados">
                                    <div className="flex items-center justify-end">
                                        <input type="checkbox" id="dead_file" checked={state.dead_file}
                                               onChange={({target: {checked}}) => setState(prev => ({...prev, dead_file: checked}))}
                                               className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-0 cursor-pointer"/>
                                        <label htmlFor="dead_file"
                                               className="flex items-center gap-2 ml-2 text-gray-700 text-lg cursor-pointer select-none"
                                               title="Essa flag deve ser usada quando o Sentry for desativado ou removido">
                                            Arquivo morto <FaQuestionCircle/>
                                        </label>
                                    </div>

                                    <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12 mt-4">

                                        <InputForm id="name" name="name" value={state.name} onChange={handleInput} label="Nome *"
                                                   col="col-span-5"/>

                                        <Button target={"primary"} col={"col-span-1"}
                                                disabled={!id}
                                                title={"Iniciar conexão SSH"}
                                                onClick={openSsh}>
                                            <FaExternalLinkAlt/>
                                        </Button>

                                        <InputDateTime
                                            id="activation_date"
                                            selected={state.activation_date ? moment(state.activation_date).toDate() : null}
                                            onChange={(date) => handleDateInput("activation_date", date)}
                                            label="Data de ativação"
                                            col="col-span-2"
                                        />

                                        <InputDateTime
                                            id="expiration_date"
                                            selected={state.expiration_date ? moment(state.expiration_date).toDate() : null}
                                            onChange={handleExpirationDateChange}
                                            onBlur={handleExpirationDateBlur}
                                            filterDate={isExpirationDateAllowed}
                                            excludeDates={excludedExpirationDates}
                                            label="Data de expiração"
                                            col="col-span-2"
                                        />

                                        <InputDateTime
                                            id="answer"
                                            selected={state.answer ? moment(state.answer).toDate() : null}
                                            onChange={(date) => handleDateInput("answer", date)}
                                            label="Podemos atender até"
                                            col="col-span-2"
                                        />

                                        <div className="col-span-5 text-gray-900">
                                            <SelectComponent
                                                instanceId="commercial_contact_id"
                                                label="Contato comercial"
                                                options={commercialOpt}
                                                value={state.commercial_contact_id}
                                                onChange={(value) => handleSelect("commercial_contact_id", value)}
                                            />
                                        </div>

                                        <div className="col-span-5 text-gray-900">
                                            <SelectComponent
                                                instanceId="resales"
                                                label="Revendas relacionadas"
                                                options={resales ?? []}
                                                value={state.resales}
                                                onChange={(value) => handleSelect("resales", value)}
                                                isMulti
                                            />
                                        </div>

                                        <InputForm id="phone" name="phone" value={state.phone} onBlur={handleInputMask} label="Telefone"
                                                   mask={MaskDefault.phone} whatsApp={true}
                                                   col="col-span-2"/>

                                        <div className="col-span-3 text-gray-900">
                                            <SelectComponent
                                                instanceId="type_license"
                                                label="Tipo da licença"
                                                options={[
                                                    {value: "ADVANCED", label: "ADVANCED"},
                                                    {value: "BASIC", label: "BASIC"},
                                                    {value: "ENHANCED", label: "ENHANCED"},
                                                    {value: "ENTERPRISE", label: "ENTERPRISE"},
                                                    {value: "STARTER", label: "STARTER"},
                                                ]}
                                                value={state.type_license}
                                                onChange={(value) => handleSelect("type_license", value)}
                                                name="type_license"
                                            />
                                        </div>

                                        <div className="col-span-2 text-gray-900">
                                            <SelectComponent
                                                instanceId="modality"
                                                label="Modalidade"
                                                options={[
                                                    {value: "LOCATION", label: "LOCAÇÃO"},
                                                    {value: "ACQUISITION", label: "AQUISIÇÃO"},
                                                    {value: "DEMONSTRATION", label: "DEMONSTRAÇÃO"},
                                                    {value: "DEVELOPMENT", label: "DESENVOLVIMENTO"},
                                                    {value: "RELATED", label: "RELACIONADA"}
                                                ]}
                                                value={state.modality}
                                                onChange={(value) => handleSelect("modality", value)}
                                                name="modality"
                                            />
                                        </div>

                                        {
                                            (state.modality?.value === "RELATED" || state.modality === "RELATED") &&
                                            <>
                                                <div className="col-span-3 text-gray-900">
                                                    <SelectComponent
                                                        instanceId="related_ibge"
                                                        label="Projeto relacionado"
                                                        options={options?.related_ibge ?? []}
                                                        value={state.related_ibge}
                                                        onChange={(value) => handleSelect("related_ibge", value)}
                                                        name="related_ibge"
                                                    />
                                                </div>

                                                <div className="col-span-2 text-gray-900">
                                                    <SelectComponent
                                                        instanceId="type_project"
                                                        label="Tipo"
                                                        options={[
                                                            {value: "DC", label: "Defesa Civil"},
                                                            {value: "OU", label: "Outro"},
                                                            {value: "TR", label: "Trânsito"}
                                                        ]}
                                                        value={state.type_project}
                                                        onChange={(value) => handleSelect("type_project", value)}
                                                        name="type_project"
                                                    />
                                                </div>
                                            </>
                                        }

                                        <InputForm id="code" value={state.code ?? ""} label="ID *" col="col-span-2" disabled/>

                                        <div className="col-span-2 text-gray-900">
                                            <SelectComponent
                                                instanceId="category"
                                                label="Categoria"
                                                options={[
                                                    {value: "city_cat", label: "Cidade"},
                                                    {value: "state_cat", label: "Estado"},
                                                    {value: "others_cat", label: "Outros"}
                                                ]}
                                                value={state.category}
                                                onChange={(value) => handleSelect("category", value)}
                                                name="category"
                                            />
                                        </div>

                                        <div className="col-span-3 text-gray-900">
                                            <SelectComponent
                                                instanceId="department"
                                                label="Departamento"
                                                options={[
                                                    {value: "security_dep", label: "Secretaria de Segurança"},
                                                    {value: "transit_dep", label: "Secretaria de Trânsito"},
                                                    {value: "others_dep", label: "Outros"}
                                                ]}
                                                value={state.department}
                                                onChange={(value) => handleSelect("department", value)}
                                                name="department"
                                            />
                                        </div>


                                        <div className="flex gap-4 col-span-7">
                                            <InputForm id="observation" name="observation" label="Observação" col="w-full" value={state.observation} onChange={handleInput}/>

                                            <InputCheckbox id="resale_only" name="resale_only" label="Atendimento apenas pela revenda" col={"w-[345px]"}
                                                           onChange={({ target: { checked } }) => setState(prev => ({
                                                               ...prev,
                                                               resale_only: checked
                                                           }))}
                                                           checked={state.resale_only} />
                                        </div>



                                        <InputText
                                            id="info"
                                            name="info"
                                            value={state.info ?? ""}
                                            onChange={handleInput}
                                            label="Rede e acessos"
                                            height={"h-[calc(100vh-510px)]"}
                                            col="col-span-6"/>

                                        <InputText
                                            id="ssh_warning"
                                            name="ssh_warning"
                                            value={state.ssh_warning ?? ""}
                                            onChange={handleInput}
                                            label="Avisos"
                                            height={"h-[calc(100vh-510px)]"}
                                            col="col-span-6"/>
                                    </div>

                                </Card>

                            </>
                        }

                        <LicenceTable
                            wrapperClass={showCard === "license" && options?.license_permission ? "block" : "hidden"}
                            licences={state.project_licensing}
                            gridRef={licencesRef}
                            id={id}
                            code={state.code ?? ""}
                            totalActiveLane={state?.total_active_lane}
                            handleInput={handleInput}
                            onClose={onClose}
                            submit={submit}
                            onlyClose={onlyClose}
                        />

                        <Card title="Contatos" wrapperClass={showCard === "contacts" ? "block" : "hidden" }>
                            <ContactsTable contacts={state.contacts} gridRef={contactsRef} id={id} onClose={onClose} submit={submit} onlyClose={onlyClose}/>
                        </Card>

                        <Card title="Acesso SSH aos servidores" wrapperClass={showCard === "ssh" ? "block" : "hidden" }>
                            <SshTable data={state.project_ssh} gridRef={sshRef} onClose={onClose} submit={submit} onlyClose={onlyClose}/>
                        </Card>

                        <Card title={"Documentos"} wrapperClass={showCard === "docs" ? "block" : "hidden"}>
                            <AttachesTable attaches={state.attachments} gridRef={attachesRef} wrapperClass="" header={false} submit={submit}/>
                        </Card>

                        <Card title={"Inventário"} wrapperClass={showCard === "inventory" ? "block" : "hidden"}>
                            <span style={{ whiteSpace: 'nowrap', marginRight: '1rem' }} className="text-lg">Equipamentos</span>
                            <div style={{ flex: 1, borderBottom: '1px solid #ccc' }}></div>
                            <EquipmentTable equipment={state.equipment} gridRef={equipmentRef} wrapperClass={"mb-5"} onClose={onClose} submit={submit} onlyClose={onlyClose}/>

                            <AttachesTable attaches={state.link_attachments} gridRef={linkAttachesRef} fileId={"install-file-input"} wrapperClass={"mt-5 mb-5"} submit={submit}/>

                            <span style={{ whiteSpace: 'nowrap', marginRight: '1rem' }} className="text-lg">Outras informações</span>
                            <div style={{ flex: 1, borderBottom: '1px solid #ccc' }}></div>

                            <InputText textarearef={multiwayLinkRef}
                                       handleKeyDown={handleKeyDown}
                                       fontFamily={"font-consolas"}
                                       id="multiway_link"
                                       name="multiway_link"
                                       value={state.multiway_link ?? ""}
                                       onChange={handleInput}
                                       label="Link da Multiway"
                                       col="col-span-2 mt-6"/>

                            <InputText textarearef={installObservationRef}
                                       handleKeyDown={handleKeyDown}
                                       fontFamily={"font-consolas"}
                                       id="install_observation"
                                       name="install_observation"
                                       value={state.install_observation ?? ""}
                                       onChange={handleInput}
                                       label="Observação"
                                       col="col-span-2 mt-6"/>

                        </Card>

                        <Card title="Clone" wrapperClass={showCard === "clone" ? "block" : "hidden" }>
                            <CloneComponent state={state} />
                        </Card>

                        {!isReadOnlyProject && <Card wrapperClass={(showCard === "clone" ? "hidden " : "block") + " mt-5" }>
                            <div className="flex justify-end gap-3">
                                {
                                    id && excludePermission &&
                                    <Button onClick={deleteProject} target="danger">
                                        Excluir
                                    </Button>
                                }
                                <Button onClick={cancel} target="warning">
                                    Cancelar
                                </Button>
                                <Button onClick={submit} target="success">
                                    Salvar
                                </Button>
                            </div>
                        </Card>}
                    </div>

                </div>
            </div>

            <SshModal open={sshModal} set={setSshModal} project={state.projectSsh}/>
        </TemplateDefault>
    );
};

export default ProjectForm;
